import type { ChannelSummary } from '@pulsify/shared'
import { Copy, Printer } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { useShareLinks } from '~/entities/share-link/model/use-share-links'
import { env } from '~/env'
import { type FlyerLocale, qrShareUrl } from '~/features/events/checkin-flyer/flyer-content'
import { useFlyerPrint } from '~/features/place-flyers/hooks/use-flyer-print'
import type { PrintSize } from '~/features/place-flyers/lib/place-flyer-content'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { copyToClipboard } from '~/shared/lib/share'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { QrPngButton } from '~/shared/ui/qr-download'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Skeleton } from '~/shared/ui/skeleton'
import { buildFollowFlyerContent, followQrUrl } from '../lib/follow-flyer-content'
import { FollowFlyer } from './follow-flyer'

const SIZE_LABELS: Record<PrintSize, () => string> = {
  a4: m.placeFlyersSizeA4,
  a5: m.placeFlyersSizeA5,
  a6: m.placeFlyersSizeA6,
}

/** A printable flyer and QR code that lead people to follow this organizer page. */
export function FollowQrCard({
  businessId,
  channel,
}: {
  businessId: string
  channel: ChannelSummary
}) {
  const shareLinks = useShareLinks(businessId, { kind: 'profile', id: channel.id })
  const [size, setSize] = useState<PrintSize>('a5')
  const [locale, setLocale] = useState<FlyerLocale>(() => (getLocale() === 'en' ? 'en' : 'de'))
  const { print, portal } = useFlyerPrint()

  const url = followQrUrl(qrShareUrl(shareLinks.data), env.VITE_PUBLIC_APP_URL, channel.handle)
  const content = buildFollowFlyerContent(channel, url, locale)
  const sizeLabel = SIZE_LABELS[size]()
  const onPrint = () => print(size, <FollowFlyer content={content} className="size-full" />)
  const copy = async () => {
    if (await copyToClipboard(url)) toast.success(m.linkCopied())
    else toast.error(m.failedToCopyLink())
  }

  return (
    <Card className="@container flex w-full flex-col gap-4 p-4">
      <h3 className="font-semibold font-serif">{m.organizerFollowQrTitle()}</h3>
      {shareLinks.isLoading ? (
        <Skeleton className="h-64 w-full rounded-lg" />
      ) : (
        <div className="flex flex-col gap-4 @lg:flex-row">
          <button
            type="button"
            onClick={onPrint}
            aria-label={m.organizerFollowQrPreview({ size: sizeLabel })}
            className="flex shrink-0 justify-center rounded-lg bg-muted p-3 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none @lg:w-48"
          >
            <FollowFlyer
              content={content}
              className="pointer-events-none aspect-[148/210] w-full max-w-48 rounded-md shadow-lg"
            />
          </button>
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <p className="text-muted-foreground text-sm">{m.organizerFollowQrDescription()}</p>
            <div className="flex flex-wrap gap-2">
              <SegmentedControl
                label={m.placeFlyersSize()}
                value={size}
                onChange={setSize}
                options={(['a4', 'a5', 'a6'] as const).map((value) => ({
                  value,
                  label: SIZE_LABELS[value](),
                }))}
              />
              <SegmentedControl
                label={m.placeFlyersLanguage()}
                value={locale}
                onChange={setLocale}
                options={[
                  { value: 'de', label: m.placeFlyersLanguageDe() },
                  { value: 'en', label: m.placeFlyersLanguageEn() },
                ]}
              />
            </div>
            <p className="truncate font-mono text-muted-foreground text-xs">
              {url.replace(/^https?:\/\//, '')}
            </p>
            <div className="mt-auto flex flex-col gap-2 @lg:flex-row @lg:flex-wrap">
              <Button onClick={onPrint}>
                <Printer className="mr-2 h-4 w-4" />
                {m.placeFlyersPrint({ size: sizeLabel })}
              </Button>
              <QrPngButton
                value={url}
                fileName={`pulsify-folgen-${channel.handle}`}
                label={m.organizerFollowQrDownload()}
              />
              <Button variant="outline" onClick={copy}>
                <Copy className="mr-2 h-4 w-4" />
                {m.organizerFollowQrCopy()}
              </Button>
            </div>
          </div>
        </div>
      )}
      {portal}
    </Card>
  )
}
